import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ArrowLeft, FileText, Coins, Users, Wallet, CheckCircle2, ShoppingBag, ChevronLeft, ChevronRight, Eye, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import OrdersTable from "../components/OrdersTable";

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;

export default function AdminReport() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [companies, setCompanies] = useState([]);
  const [pageT, setPageT] = useState(1);
  const [pageP, setPageP] = useState(1);
  const [pageR, setPageR] = useState(1);
  const [statusP, setStatusP] = useState("pending");
  const [sourceP, setSourceP] = useState("all");
  const [transactional, setTransactional] = useState(null);
  const [payables, setPayables] = useState(null);
  const [recharges, setRecharges] = useState(null);
  const [payableDetail, setPayableDetail] = useState(null);

  useEffect(() => { api.get("/companies?include_inactive=true").then((r) => setCompanies(r.data || [])).catch(() => {}); }, []);

  const params = (extra = {}) => {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    if (companyFilter && companyFilter !== "all") p.set("company_id", companyFilter);
    Object.entries(extra).forEach(([k, v]) => { if (v !== undefined && v !== "" && v !== "all") p.set(k, v); });
    return p.toString();
  };

  const loadAll = async () => {
    try {
      const [t, p, r] = await Promise.all([
        api.get(`/admin/reports/transactional?${params({ page: pageT, page_size: 50 })}`),
        api.get(`/admin/reports/payables?${params({ page: pageP, page_size: 50, status: statusP, source: sourceP })}`),
        api.get(`/admin/reports/recharges?${params({ page: pageR, page_size: 50 })}`),
      ]);
      setTransactional(t.data);
      setPayables(p.data);
      setRecharges(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [pageT, pageP, pageR, statusP, sourceP]);

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/reports/payables/${id}/mark-paid`);
      toast.success("Marcado como pago");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/admin" className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mb-4" data-testid="back-admin"><ArrowLeft size={14}/> Voltar ao Admin</Link>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><FileText size={28}/> Relatórios Financeiros</h1>
      <p className="text-stone-500 mt-1 text-sm">Visões transacional, contas a pagar e reabastecimentos.</p>

      <Card className="mt-6 p-4 rounded-2xl border-stone-200 flex flex-wrap items-end gap-3">
        <div><Label>De</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl mt-1" data-testid="rpt-from"/></div>
        <div><Label>Até</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl mt-1" data-testid="rpt-to"/></div>
        <div className="min-w-[200px]">
          <Label>Empresa</Label>
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="rounded-xl mt-1" data-testid="rpt-company"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={() => { setPageT(1); setPageP(1); setPageR(1); loadAll(); }} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="rpt-apply">Aplicar</Button>
      </Card>

      <Tabs defaultValue="transactional" className="mt-6">
        <TabsList className="bg-stone-100 rounded-xl h-auto flex-wrap">
          <TabsTrigger value="transactional" data-testid="tab-transactional"><Coins size={14} className="mr-1"/> Transacional</TabsTrigger>
          <TabsTrigger value="payables" data-testid="tab-payables"><Wallet size={14} className="mr-1"/> Contas a Pagar</TabsTrigger>
          <TabsTrigger value="recharges" data-testid="tab-recharges"><Users size={14} className="mr-1"/> Reabastecimentos</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingBag size={14} className="mr-1"/> Vendas Checkout</TabsTrigger>
        </TabsList>

        <TabsContent value="transactional" className="mt-6 space-y-5">
          {transactional && (<>
            <div className="grid sm:grid-cols-4 gap-4">
              <Card className="p-4 rounded-2xl bg-emerald-50 border-emerald-200"><div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Cashback emitido</div><div className="font-display text-2xl text-emerald-900 mt-1">{fmt(transactional.totals.issued)}</div></Card>
              <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200"><div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Cashback resgatado</div><div className="font-display text-2xl text-amber-900 mt-1">{fmt(transactional.totals.redeemed)}</div></Card>
              <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200"><div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Receita (taxa de resgate)</div><div className="font-display text-2xl text-blue-950 mt-1">{fmt(transactional.totals.platform_revenue_redeem)}</div></Card>
              <Card className="p-4 rounded-2xl bg-stone-100 border-stone-200"><div className="text-[10px] uppercase tracking-wider text-stone-700 font-bold">Transações</div><div className="font-display text-2xl text-stone-900 mt-1">{transactional.totals.transactions}</div></Card>
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="p-5 rounded-2xl border-stone-200">
                <h3 className="font-display text-lg font-semibold mb-3">Top empresas (cashback emitido)</h3>
                <div className="divide-y divide-stone-100">
                  {transactional.top_sellers.length === 0 && <p className="text-sm text-stone-500">Sem dados.</p>}
                  {transactional.top_sellers.map((s, i) => (
                    <div key={s.company_id} className="py-2.5 flex items-center justify-between gap-2">
                      <div className="text-sm"><span className="font-bold text-stone-500 mr-2">#{i + 1}</span>{s.company_name}</div>
                      <div className="font-display font-bold text-blue-950">{fmt(s.total_issued)} <span className="text-[10px] text-stone-500 ml-1">({s.count}tx)</span></div>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="p-5 rounded-2xl border-stone-200">
                <h3 className="font-display text-lg font-semibold mb-3">Top clientes</h3>
                <div className="divide-y divide-stone-100">
                  {transactional.top_customers.length === 0 && <p className="text-sm text-stone-500">Sem dados.</p>}
                  {transactional.top_customers.map((c, i) => (
                    <div key={c.customer_id} className="py-2.5 flex items-center justify-between gap-2">
                      <div className="text-sm"><span className="font-bold text-stone-500 mr-2">#{i + 1}</span>{c.customer_name || c.customer_id}</div>
                      <div className="font-display font-bold text-emerald-700">{fmt(c.total_received)} <span className="text-[10px] text-stone-500 ml-1">({c.count}tx)</span></div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <Card className="p-5 rounded-2xl border-stone-200">
              <h3 className="font-display text-lg font-semibold mb-3">Transações ({transactional.total || transactional.transactions.length})</h3>
              <div className="overflow-auto -mx-2">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-stone-500 text-[11px] uppercase border-b border-stone-200">
                    <th className="px-2 py-2">Data</th><th>Empresa</th><th>Cliente</th><th>Tipo</th><th className="text-right">Cashback</th><th className="text-right">Compra</th>
                  </tr></thead>
                  <tbody className="divide-y divide-stone-100">
                    {transactional.transactions.map((t) => (
                      <tr key={t.id}>
                        <td className="px-2 py-2 text-[11px] text-stone-500">{new Date(t.created_at).toLocaleString("pt-BR")}</td>
                        <td>{t.company_name}</td>
                        <td>{t.customer_name || "-"}</td>
                        <td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${t.type === "CREDIT" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>{t.type === "CREDIT" ? "EMISSÃO" : "RESGATE"}</span></td>
                        <td className="text-right font-bold">{fmt(t.cashback_value)}</td>
                        <td className="text-right text-stone-600">{fmt(t.purchase_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {transactional.pages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-4">
                  <Button variant="outline" size="sm" disabled={pageT <= 1} onClick={() => setPageT(pageT - 1)} className="rounded-lg"><ChevronLeft size={14}/></Button>
                  <span className="text-sm text-stone-600">Página {transactional.page} de {transactional.pages}</span>
                  <Button variant="outline" size="sm" disabled={pageT >= transactional.pages} onClick={() => setPageT(pageT + 1)} className="rounded-lg"><ChevronRight size={14}/></Button>
                </div>
              )}
            </Card>
          </>)}
        </TabsContent>

        <TabsContent value="payables" className="mt-6 space-y-5">
          {payables && (<>
            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200"><div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Total a pagar</div><div className="font-display text-2xl text-amber-900 mt-1">{fmt(payables.totals.total_due)}</div></Card>
              <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200"><div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Taxa retida</div><div className="font-display text-2xl text-blue-950 mt-1">{fmt(payables.totals.total_fee_retained)}</div></Card>
              <Card className="p-4 rounded-2xl bg-stone-100 border-stone-200"><div className="text-[10px] uppercase tracking-wider text-stone-700 font-bold">Pagamentos pendentes</div><div className="font-display text-2xl text-stone-900 mt-1">{payables.totals.count}</div></Card>
            </div>

            <Card className="p-5 rounded-2xl border-stone-200">
              <h3 className="font-display text-lg font-semibold mb-3">Resumo por empresa</h3>
              <div className="divide-y divide-stone-100">
                {payables.by_company.length === 0 && <p className="text-sm text-stone-500">Nada a pagar.</p>}
                {payables.by_company.map((c) => (
                  <div key={c.company_id} className="py-2.5 flex items-center justify-between gap-2">
                    <div className="text-sm font-medium">{c.company_name}</div>
                    <div className="text-right">
                      <div className="font-display font-bold text-amber-900">{fmt(c.total_due)}</div>
                      <div className="text-[11px] text-stone-500">taxa retida {fmt(c.total_fee)} · {c.count} resgates</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5 rounded-2xl border-stone-200">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <h3 className="font-display text-lg font-semibold">Pagamentos</h3>
                <div className="flex gap-2">
                  <Select value={statusP} onValueChange={(v) => { setPageP(1); setStatusP(v); }}>
                    <SelectTrigger className="rounded-xl w-[140px]" data-testid="payable-status"><SelectValue/></SelectTrigger>
                    <SelectContent><SelectItem value="pending">Pendentes</SelectItem><SelectItem value="paid">Pagos</SelectItem><SelectItem value="all">Todos</SelectItem></SelectContent>
                  </Select>
                  <Select value={sourceP} onValueChange={(v) => { setPageP(1); setSourceP(v); }}>
                    <SelectTrigger className="rounded-xl w-[170px]" data-testid="payable-source"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas origens</SelectItem>
                      <SelectItem value="order">Vendas (Checkout)</SelectItem>
                      <SelectItem value="redemption">Resgates comuns</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-stone-500 text-[11px] uppercase border-b border-stone-200">
                    <th className="px-2 py-2">Data</th><th>Empresa</th><th>Origem</th><th className="text-right">Valor</th><th className="text-right">Taxa</th><th></th>
                  </tr></thead>
                  <tbody className="divide-y divide-stone-100">
                    {payables.payouts.map((p) => (
                      <tr key={p.id} data-testid={`payout-${p.id}`}>
                        <td className="px-2 py-2 text-[11px] text-stone-500">{new Date(p.created_at).toLocaleString("pt-BR")}</td>
                        <td>{p.company_name}</td>
                        <td><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.source === "order" ? "bg-blue-100 text-blue-900" : p.source === "redemption" ? "bg-amber-100 text-amber-900" : "bg-stone-100 text-stone-600"}`}>{p.source === "order" ? "Checkout" : p.source === "redemption" ? "Resgate" : "—"}</span></td>
                        <td className="text-right font-bold">{fmt(p.amount)}</td>
                        <td className="text-right text-stone-600">{fmt(p.admin_fee)}</td>
                        <td className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={async () => { try { const r = await api.get(`/admin/reports/payables/${p.id}`); setPayableDetail(r.data); } catch (e) { toast.error("Falha ao carregar"); } }} className="rounded-lg text-xs mr-1" data-testid={`detail-${p.id}`}><Eye size={12}/></Button>
                          {p.status === "pending"
                            ? <Button size="sm" variant="outline" onClick={() => markPaid(p.id)} className="rounded-lg text-xs" data-testid={`mark-paid-${p.id}`}><CheckCircle2 size={12} className="mr-1"/>Marcar pago</Button>
                            : <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md">Pago</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {payables.pages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-4">
                  <Button variant="outline" size="sm" disabled={pageP <= 1} onClick={() => setPageP(pageP - 1)} className="rounded-lg"><ChevronLeft size={14}/></Button>
                  <span className="text-sm text-stone-600">Página {payables.page} de {payables.pages}</span>
                  <Button variant="outline" size="sm" disabled={pageP >= payables.pages} onClick={() => setPageP(pageP + 1)} className="rounded-lg"><ChevronRight size={14}/></Button>
                </div>
              )}
            </Card>

            {payableDetail && (
              <Dialog open onOpenChange={(v) => !v && setPayableDetail(null)}>
                <DialogContent className="max-w-2xl rounded-2xl">
                  <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Wallet size={20}/> Detalhe do pagamento</DialogTitle></DialogHeader>
                  <div className="space-y-3 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div><span className="text-stone-500">Empresa</span><p className="font-semibold">{payableDetail.payout.company_name}</p></div>
                      <div><span className="text-stone-500">Valor a pagar</span><p className="font-display text-2xl font-bold text-amber-900">{fmt(payableDetail.payout.amount)}</p></div>
                      <div><span className="text-stone-500">Taxa retida</span><p className="font-semibold">{fmt(payableDetail.payout.admin_fee)}</p></div>
                      <div><span className="text-stone-500">Status</span><p>{payableDetail.payout.status}</p></div>
                    </div>
                    <div className="p-3 rounded-xl bg-stone-50 border border-stone-200">
                      <p className="text-xs font-bold uppercase tracking-wider text-stone-700">Origem da transação</p>
                      {payableDetail.source === "order" && (
                        <>
                          <p className="mt-2 text-blue-900 font-semibold">🛒 Venda pelo Checkout MorarBem</p>
                          {payableDetail.linked_order && (
                            <div className="mt-2 text-xs space-y-1 text-stone-700">
                              <p>Pedido <strong>#{payableDetail.linked_order.id.slice(0,8)}</strong></p>
                              <p>Cliente: {payableDetail.linked_order.customer_name} ({payableDetail.linked_order.customer_phone})</p>
                              <p>Subtotal: {fmt(payableDetail.linked_order.subtotal)} · BemCash usado: {fmt(payableDetail.linked_order.cashback_used)} · MP: {fmt(payableDetail.linked_order.mp_amount)}</p>
                              <p>Taxa do cartão paga pelo cliente: {fmt(payableDetail.linked_order.customer_gateway_fee)}</p>
                              <p className="text-stone-500">Itens: {(payableDetail.linked_order.items || []).map((i) => `${i.qty}× ${i.title}`).join(", ")}</p>
                            </div>
                          )}
                        </>
                      )}
                      {payableDetail.source === "redemption" && (
                        <>
                          <p className="mt-2 text-amber-900 font-semibold">💸 Resgate de cashback (cliente usou saldo no balcão)</p>
                          {payableDetail.linked_redemption && (
                            <div className="mt-2 text-xs space-y-1 text-stone-700">
                              <p>Resgate <strong>#{payableDetail.linked_redemption.id.slice(0,8)}</strong></p>
                              <p>Cliente usou: {fmt(payableDetail.linked_redemption.cashback_to_use)} de BemCash</p>
                              <p>Valor da compra: {fmt(payableDetail.linked_redemption.items_value)}</p>
                            </div>
                          )}
                        </>
                      )}
                      {!payableDetail.source && <p className="text-stone-500 mt-2 text-xs">Sem origem identificada (pré-rastreamento).</p>}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    <Button variant="outline" onClick={() => setPayableDetail(null)}>Fechar</Button>
                    {payableDetail.payout.status === "pending" && (
                      <Button onClick={async () => { await markPaid(payableDetail.payout.id); setPayableDetail(null); }} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="detail-mark-paid">
                        <CheckCircle2 size={14} className="mr-1.5"/> Marcar como pago
                      </Button>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </>)}
        </TabsContent>

        <TabsContent value="recharges" className="mt-6 space-y-5">
          {recharges && (<>
            <div className="grid sm:grid-cols-3 gap-4">
              <Card className="p-4 rounded-2xl bg-emerald-50 border-emerald-200"><div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Confirmadas</div><div className="font-display text-2xl text-emerald-900 mt-1">{fmt(recharges.totals.total_confirmed)}</div></Card>
              <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200"><div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Pendentes</div><div className="font-display text-2xl text-amber-900 mt-1">{fmt(recharges.totals.total_pending)}</div></Card>
              <Card className="p-4 rounded-2xl bg-stone-100 border-stone-200"><div className="text-[10px] uppercase tracking-wider text-stone-700 font-bold">Total transações</div><div className="font-display text-2xl text-stone-900 mt-1">{recharges.totals.count}</div></Card>
            </div>

            <Card className="p-5 rounded-2xl border-stone-200">
              <h3 className="font-display text-lg font-semibold mb-3">Reabastecimentos por empresa</h3>
              <div className="divide-y divide-stone-100">
                {recharges.by_company.length === 0 && <p className="text-sm text-stone-500">Sem dados.</p>}
                {recharges.by_company.map((c) => (
                  <div key={c.company_id} className="py-2.5 flex items-center justify-between gap-2">
                    <div className="text-sm font-medium">{c.company_name}</div>
                    <div className="font-display font-bold text-emerald-700">{fmt(c.total_recharged)} <span className="text-[10px] text-stone-500 ml-1">({c.count}x)</span></div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5 rounded-2xl border-stone-200">
              <h3 className="font-display text-lg font-semibold mb-3">Todas as recargas</h3>
              <div className="overflow-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-stone-500 text-[11px] uppercase border-b border-stone-200">
                    <th className="px-2 py-2">Data</th><th>Empresa</th><th className="text-right">Valor</th><th>Status</th>
                  </tr></thead>
                  <tbody className="divide-y divide-stone-100">
                    {recharges.recharges.map((r) => (
                      <tr key={r.id}>
                        <td className="px-2 py-2 text-[11px] text-stone-500">{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                        <td>{r.company_name}</td>
                        <td className="text-right font-bold">{fmt(r.amount)}</td>
                        <td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${r.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {recharges.pages > 1 && (
                <div className="flex justify-center items-center gap-3 mt-4">
                  <Button variant="outline" size="sm" disabled={pageR <= 1} onClick={() => setPageR(pageR - 1)} className="rounded-lg"><ChevronLeft size={14}/></Button>
                  <span className="text-sm text-stone-600">Página {recharges.page} de {recharges.pages}</span>
                  <Button variant="outline" size="sm" disabled={pageR >= recharges.pages} onClick={() => setPageR(pageR + 1)} className="rounded-lg"><ChevronRight size={14}/></Button>
                </div>
              )}
            </Card>
          </>)}
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          <OrdersTable scope="admin"/>
        </TabsContent>
      </Tabs>
    </div>
  );
}
